import { isStandalonePwa, setupPwaInstallHint } from "./pwa-install.js";

const BUILD_ID = import.meta.env.VITE_ENDCLOCK_BUILD_ID as string;
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1_000;

interface PwaDebugSurface {
  readonly buildId: string;
  readonly standalone: boolean;
  checkForUpdate: () => Promise<void>;
  requireUpdate: () => void;
}

declare global {
  interface Window {
    __ENDCLOCK_PWA__: PwaDebugSurface;
  }
}

export function setupPwa(): PwaDebugSurface {
  setupPwaInstallHint();
  const buildLabel = document.querySelector<HTMLElement>("#build-version");
  const notice = document.querySelector<HTMLElement>("#update-notice");
  const updateDetail = document.querySelector<HTMLElement>("#update-detail");
  const updateNow = document.querySelector<HTMLButtonElement>("#update-now");
  const updateLater = document.querySelector<HTMLButtonElement>("#update-later");
  if (!buildLabel || !notice || !updateDetail || !updateNow || !updateLater) throw new Error("Missing PWA interface");

  buildLabel.textContent = `BUILD ${BUILD_ID}`;

  let registration: ServiceWorkerRegistration | undefined;
  let waitingWorker: ServiceWorker | undefined;
  let activationPending = false;
  let reloadStarted = false;
  let updateRequired = false;
  let controllerEstablished = "serviceWorker" in navigator && navigator.serviceWorker.controller !== null;

  const offerUpdate = (worker = registration?.waiting ?? undefined): void => {
    if (!worker || !navigator.serviceWorker.controller) return;
    waitingWorker = worker;
    if (!activationPending) {
      notice.dataset.state = updateRequired ? "required" : "ready";
      updateDetail.textContent = updateRequired ? "UPDATE REQUIRED — READY" : "NEW RELEASE READY";
      updateNow.disabled = false;
      updateLater.disabled = updateRequired;
    }
    notice.hidden = false;
  };

  const checkForUpdate = async (): Promise<void> => {
    if (!registration || !navigator.onLine) return;
    try {
      await registration.update();
    } catch {
      // A downloaded waiting worker remains actionable even if this network check fails.
    }
    offerUpdate();
  };

  const requireUpdate = (): void => {
    updateRequired = true;
    notice.dataset.state = "required";
    updateDetail.textContent = "UPDATE REQUIRED — CHECKING";
    updateNow.disabled = waitingWorker === undefined && registration?.waiting === undefined;
    updateLater.disabled = true;
    notice.hidden = false;
    void checkForUpdate();
  };

  const controller = { buildId: BUILD_ID, get standalone() { return isStandalonePwa(); }, checkForUpdate, requireUpdate };
  window.__ENDCLOCK_PWA__ = controller;

  updateLater.addEventListener("click", () => {
    notice.hidden = true;
  });

  updateNow.addEventListener("click", () => {
    const worker = registration?.waiting ?? waitingWorker;
    if (!worker || activationPending) return;
    activationPending = true;
    updateNow.disabled = true;
    updateLater.disabled = true;
    notice.dataset.state = "checking";
    updateDetail.textContent = "REQUESTING UPDATE";

    const channel = new MessageChannel();
    const responseTimeout = window.setTimeout(() => {
      activationPending = false;
      notice.dataset.state = "blocked";
      updateDetail.textContent = "UPDATE CHECK FAILED — TRY AGAIN";
      updateNow.disabled = false;
      updateLater.disabled = false;
    }, 5_000);
    channel.port1.addEventListener("message", (event: MessageEvent<unknown>) => {
      if (!event.data || typeof event.data !== "object" || !("type" in event.data)) return;
      const response = event.data as { readonly type: string };
      window.clearTimeout(responseTimeout);
      if (response.type === "UPDATE_ACTIVATING") {
        notice.dataset.state = "activating";
        updateDetail.textContent = "ACTIVATING NEW RELEASE";
      }
    });
    channel.port1.start();
    worker.postMessage("SKIP_WAITING", [channel.port2]);
  });

  if (!("serviceWorker" in navigator) || !window.isSecureContext || !import.meta.env.PROD) return controller;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!controllerEstablished) {
      controllerEstablished = true;
      return;
    }
    if (reloadStarted) return;
    reloadStarted = true;
    location.reload();
  });

  const observeInstalling = (worker: ServiceWorker): void => {
    worker.addEventListener("statechange", () => {
      if (worker.state === "installed") offerUpdate(worker);
    });
  };

  const register = async (): Promise<void> => {
    try {
      // The homepage PWA launches the same game without leaving its origin.
      const scope = location.pathname.startsWith("/play/") ? "/play/" : "/";
      registration = await navigator.serviceWorker.register(`${scope}sw.js`, {
        scope,
        updateViaCache: "none",
      });
      if (registration.installing) observeInstalling(registration.installing);
      registration.addEventListener("updatefound", () => {
        if (registration?.installing) observeInstalling(registration.installing);
      });
      offerUpdate();
      await checkForUpdate();
    } catch {
      return;
    }
  };

  void register();
  window.addEventListener("focus", () => void checkForUpdate());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkForUpdate();
  });
  window.setInterval(() => void checkForUpdate(), UPDATE_CHECK_INTERVAL_MS);
  return controller;
}
